import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import {
  CheckCircle as ValidIcon,
  Cancel as InvalidIcon,
  Schedule as ExpiredIcon,
  QrCodeScanner as ScanIcon,
} from '@mui/icons-material';
import { certificateApi } from '../services/api';

function VerifyPage() {
  const { code: urlCode } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [inputCode, setInputCode] = useState(urlCode || '');
  const [searchCode, setSearchCode] = useState(urlCode || '');

  const { data: result, isLoading, error, refetch } = useQuery({
    queryKey: ['verify-certificate', searchCode],
    queryFn: () => certificateApi.verify(searchCode).then((r) => r.data),
    enabled: !!searchCode,
  });

  useEffect(() => {
    if (urlCode && urlCode !== searchCode) {
      setInputCode(urlCode);
      setSearchCode(urlCode);
    }
  }, [urlCode]);

  const handleVerify = () => {
    if (inputCode.trim()) {
      setSearchCode(inputCode.trim());
      if (inputCode.trim() !== urlCode) {
        navigate(`/verify/${inputCode.trim()}`, { replace: true });
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleVerify();
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        {/* Header */}
        <Box textAlign="center" mb={4}>
          <Typography variant="h4" color="primary" gutterBottom fontWeight={700}>
            RITTEC
          </Typography>
          <Typography variant="h5" gutterBottom>
            Zertifikat-Verifikation
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Überprüfen Sie die Echtheit eines RITTEC-Zertifikats
          </Typography>
        </Box>

        {/* Input Card */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Verifikationscode eingeben
            </Typography>
            <Box display="flex" gap={2}>
              <TextField
                fullWidth
                placeholder="RLP-XXXX-XXXX-XXXX"
                value={inputCode}
                onChange={(e) => setInputCode(e.target.value.toUpperCase())}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
              />
              <Button
                variant="contained"
                onClick={handleVerify}
                disabled={isLoading || !inputCode.trim()}
                sx={{ minWidth: 120 }}
              >
                {isLoading ? <CircularProgress size={24} /> : 'Prüfen'}
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Der Verifikationscode befindet sich auf dem Zertifikat unten rechts.
            </Typography>
          </CardContent>
        </Card>

        {/* Result Card */}
        {result && (
          <Card
            sx={{
              borderTop: 4,
              borderColor: result.valid
                ? 'success.main'
                : result.expired
                ? 'warning.main'
                : 'error.main',
            }}
          >
            <CardContent>
              {result.valid ? (
                <>
                  <Box display="flex" alignItems="center" gap={2} mb={3}>
                    <ValidIcon sx={{ fontSize: 48, color: 'success.main' }} />
                    <Box>
                      <Typography variant="h5" color="success.main">
                        Gültiges Zertifikat
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Dieses Zertifikat ist authentisch und gültig.
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Inhaber
                    </Typography>
                    <Typography variant="h6" gutterBottom>
                      {result.certificate?.recipientName}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      Kurs
                    </Typography>
                    <Typography variant="h6" gutterBottom>
                      {result.certificate?.courseName}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      Zertifikat
                    </Typography>
                    <Typography variant="h6" gutterBottom>
                      {result.certificate?.name}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      Ausgestellt am
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {result.certificate?.issuedAt
                        ? new Date(result.certificate.issuedAt).toLocaleDateString('de-DE', {
                            day: '2-digit',
                            month: 'long',
                            year: 'numeric',
                          })
                        : '-'}
                    </Typography>

                    {result.certificate?.expiresAt && (
                      <>
                        <Typography variant="body2" color="text.secondary">
                          Gültig bis
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                          {new Date(result.certificate.expiresAt).toLocaleDateString('de-DE', {
                            day: '2-digit',
                            month: 'long',
                            year: 'numeric',
                          })}
                        </Typography>
                      </>
                    )}

                    <Box mt={2}>
                      <Chip
                        label={`Code: ${result.certificate?.verificationCode}`}
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                </>
              ) : result.expired ? (
                <Box display="flex" alignItems="center" gap={2}>
                  <ExpiredIcon sx={{ fontSize: 48, color: 'warning.main' }} />
                  <Box>
                    <Typography variant="h5" color="warning.main">
                      Zertifikat abgelaufen
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Dieses Zertifikat war gültig, ist aber abgelaufen.
                    </Typography>
                    {result.certificate && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Abgelaufen am:{' '}
                        {new Date(result.certificate.expiresAt!).toLocaleDateString('de-DE')}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ) : (
                <Box display="flex" alignItems="center" gap={2}>
                  <InvalidIcon sx={{ fontSize: 48, color: 'error.main' }} />
                  <Box>
                    <Typography variant="h5" color="error.main">
                      Ungültiger Code
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Es wurde kein Zertifikat mit diesem Code gefunden.
                    </Typography>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Fehler bei der Überprüfung. Bitte versuchen Sie es erneut.
          </Alert>
        )}

        {/* Footer */}
        <Box textAlign="center" mt={4}>
          <Typography variant="body2" color="text.secondary">
            RITTER Technologie GmbH - Lernplattform
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Bei Fragen wenden Sie sich an support@taegener.info
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

export default VerifyPage;
